import React, { useState, useEffect, useRef, useContext, useLayoutEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation  } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { CurrentPoiContext } from '../App';
import TTSComponent from './TTSComponent';
import PoiSlider from './PoiSlider';
import navigate from './navigate';
import config from "../config";
import BackButton from './BackButton';
import FocusPanel from './FocusPanel';
import TransportPanel from './TransportPanel';
import LoadingSkeleton from './LoadingSkeleton';
import { calculateDistance } from './utils';
import MapPanel from './MapPanel';
import RouteBox from './RouteBox';
import useTranslation from './useTranslation';
import authService from '../services/auth';
import LocalIcon from './LocalIcon';
import { getOptimizedPoiImages } from '../utils/imageUtils';

const Icon = ({ name, className, style }) => (
  <LocalIcon icon={name} className={className} style={style} />
);

// Funzione per mappare la tipologia focus alle icone corrette
const getFocusIconName = (taxonomy) => {
  if (!taxonomy?.name?.[0]?.value) return 'info';
  
  const tipologia = taxonomy.name[0].value.toLowerCase();
  
  if (tipologia.includes('audio')) return 'volume_up';
  if (tipologia.includes('immagine 360') || tipologia.includes('360')) return '360';
  if (tipologia.includes('testo')) return 'text_snippet';
  if (tipologia.includes('video')) return 'video_library';
  
  // Default fallback
  return 'info';
};

const PoiDetails = React.memo(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const calculateReadingTime = (text) => {
    const wordsPerMinute = 200;
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} ${t('tempoLettura')}`;
  };
  const [poi, setPoi] = useState(null);
  const [allPois, setAllPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxonomyData, setTaxonomyData] = useState({});
  const [taxonomyPOIData, setTaxonomyPOIData] = useState({});
  const [contacts, setContacts] = useState({});
  const [showTTS, setShowTTS] = useState(false);
  const [focusPanelOpen, setFocusPanelOpen] = useState(false);
  const [associatedRoute, setAssociatedRoute] = useState(null);
  const [activeFocus, setActiveFocus] = useState(null);
  const [focusDetails, setFocusDetails] = useState({});
  const [mapPanelOpen, setMapPanelOpen] = useState(false);
  const [userDistance, setUserDistance] = useState(null);
  const [transportPanelOpen, setTransportPanelOpen] = useState(false);
  const [loadingDistance, setLoadingDistance] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [preventScroll, setPreventScroll] = useState(false);

  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

  const loadedTaxonomyIds = useRef(new Set());
  const { nid } = useParams();
  const navigateRouter = useNavigate();
  const carouselRef = useRef(null);
  const { setCurrentPoi } = useContext(CurrentPoiContext);

  // Ottimizzazione del calcolo delle distanze con memoization
  const memoizedDistances = useRef(new Map());

  // Cache globale persistente tra renderizzazioni
  const globalCache = useRef({
    taxonomyTerms: {},
    nodes: {},
    paragraphs: {},
    poiList: null,
    routeList: null
  }).current;

  // Funzione ottimizzata per il calcolo delle distanze
  const calculateMemoizedDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const key = `${lat1},${lon1}-${lat2},${lon2}`;
    if (memoizedDistances.current.has(key)) {
      return memoizedDistances.current.get(key);
    }
    const distance = calculateDistance(lat1, lon1, lat2, lon2);
    memoizedDistances.current.set(key, distance);
    return distance;
  }, []);

  const scrollWithOffset = () => {
    if (preventScroll) return;
    
    const headerOffset = -90;
    const elementPosition = 0;
    const offsetPosition = elementPosition + headerOffset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: "instant"
    });
  };

  useEffect(() => {
    scrollWithOffset();
    window.addEventListener('focus', scrollWithOffset);
    window.addEventListener('pageshow', scrollWithOffset);
    
    return () => {
      window.removeEventListener('focus', scrollWithOffset);
      window.removeEventListener('pageshow', scrollWithOffset);
    };
  }, []);

  // Ottimizzazione del calcolo del tempo di lettura con useMemo
  const readingTime = useMemo(() => {
    if (!poi?.body?.[0]?.value) return '';
    const cleanText = DOMPurify.sanitize(poi.body[0].value).replace(/<[^>]*>/g, '');
    return calculateReadingTime(cleanText);
  }, [poi?.body, t]);

  // Cache per le chiamate API
  const cache = useRef(new Map());

  // Funzione ottimizzata per il caricamento dei dati con retry logic e caching
  const fetchWithRetry = useCallback(async (url, retries = 3) => {
    // Verifica prima nella cache locale
    if (cache.current.has(url)) {
      return cache.current.get(url);
    }

    for (let i = 0; i < retries; i++) {
      try {
        const data = await authService.get(url);
        cache.current.set(url, data);
        return data;
      } catch (error) {
        console.error(`Tentativo ${i + 1} fallito:`, error);
        if (i === retries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
      }
    }
  }, []);

  // Gestione ottimizzata del caricamento dei dati
  useLayoutEffect(() => {
    const init = async () => {
      setLoading(true);
      setShowTTS(false);
      setFocusPanelOpen(false);
      setActiveFocus(null);
      scrollWithOffset();
      
      try {
        // Verifica se i dati sono già disponibili nello state della location
        const locationState = location.state;
        
        if (locationState?.selectedPOI && locationState.selectedPOI.nid[0].value === nid) {
          // Usa i dati già disponibili
          const poiData = locationState.selectedPOI;
          setPoi(poiData);
          setCurrentPoi(poiData);
          
          if (locationState.allPois) {
            setAllPois(locationState.allPois);
          }
          
          // Gestione della tassonomia POI
          const taxonomyPOIInfo = {};
          if (poiData.field_tipologia_poi?.[0]?.target_id) {
            const taxonomyId = poiData.field_tipologia_poi[0].target_id;
            
            // Verifica se è disponibile nei dati passati dalla mappa
            if (locationState.taxonomyData && locationState.taxonomyData[taxonomyId]) {
              const taxonomyItem = locationState.taxonomyData[taxonomyId];
              taxonomyPOIInfo[taxonomyId] = {
                title: taxonomyItem.title || 'Titolo non disponibile',
                icon: taxonomyItem.icon || null,
                description: taxonomyItem.description || null
              };
            } else {
              // Carica la tassonomia se non disponibile
              try {
                const taxonomyData = await fetchWithRetry(
                  config.API_ENDPOINTS.getTaxonomyTerm(taxonomyId, selectedLang)
                );
                
                if (taxonomyData?.tid?.[0]?.value) {
                  taxonomyPOIInfo[taxonomyData.tid[0].value] = {
                    title: taxonomyData.name?.[0]?.value ?? 'Titolo non disponibile',
                    icon: taxonomyData.field_icona?.[0]?.url ?? null,
                    description: taxonomyData.description?.[0]?.value ?? null
                  };
                }
              } catch (error) {
                console.error('Errore nel caricamento della tassonomia:', error);
              }
            }
          }
          setTaxonomyPOIData(taxonomyPOIInfo);
          
          // Ottimizzazione: carica contatti in parallelo
          if (poiData.field_contatti?.length > 0) {
            const contactPromises = poiData.field_contatti.map(contact => {
              const contactUrl = config.API_ENDPOINTS.getParagraph(contact.target_id, selectedLang);
              // Verifica se già in cache
              if (cache.current.has(contactUrl)) {
                return Promise.resolve(cache.current.get(contactUrl));
              }
              return fetchWithRetry(contactUrl)
                .catch(error => {
                  console.error(`Errore nel caricamento del contatto ${contact.target_id}:`, error);
                  return null;
                });
            });
            
            const contactResults = await Promise.all(contactPromises);
            setContacts(contactResults.filter(Boolean));
          }
          
          // Carica solo i dati mancanti
          await loadFocusDetails(poiData);
          
          setLoading(false);
        } else {
          // Caricamento completo
          const poiData = await fetchWithRetry(config.API_ENDPOINTS.getNode(nid, selectedLang));
          
          // Validazione dei dati
          if (!poiData || typeof poiData !== 'object') {
            throw new Error('Dati POI non validi');
          }
    
          setPoi(poiData);
          setCurrentPoi(poiData);
    
          const taxonomyPOIInfo = {};
          if (poiData.field_tipologia_poi?.[0]?.target_id) {
            try {
              const taxonomyData = await fetchWithRetry(
                config.API_ENDPOINTS.getTaxonomyTerm(poiData.field_tipologia_poi[0].target_id, selectedLang)
              );
    
              if (taxonomyData?.tid?.[0]?.value) {
                taxonomyPOIInfo[taxonomyData.tid[0].value] = {
                  title: taxonomyData.name?.[0]?.value ?? 'Titolo non disponibile',
                  icon: taxonomyData.field_icona?.[0]?.url ?? null,
                  description: taxonomyData.description?.[0]?.value ?? null
                };
              }
            } catch (error) {
              console.error('Errore nel caricamento della tassonomia:', error);
            }
          }
    
          setTaxonomyPOIData(taxonomyPOIInfo);
    
          // Ottimizzazione: Gestione parallela delle chiamate API per i contatti
          if (poiData.field_contatti?.length > 0) {
            const contactPromises = poiData.field_contatti.map(contact => {
              const contactUrl = config.API_ENDPOINTS.getParagraph(contact.target_id, selectedLang);
              if (cache.current.has(contactUrl)) {
                return Promise.resolve(cache.current.get(contactUrl));
              }
              return fetchWithRetry(contactUrl)
                .catch(error => {
                  console.error(`Errore nel caricamento del contatto ${contact.target_id}:`, error);
                  return null;
                });
            });
            
            const contactResults = await Promise.all(contactPromises);
            setContacts(contactResults.filter(Boolean));
          }
    
          await loadFocusDetails(poiData);
          await loadAllPois();
        }
      } catch (error) {
        console.error('Errore durante il recupero dei dati:', error);
        // Gestione degli stati di errore
        setPoi(null);
        setTaxonomyPOIData({});
        setContacts({});
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [nid, setCurrentPoi, fetchWithRetry, location.state, selectedLang]);

  // Ottimizzazione della gestione della geolocalizzazione
  const handleGeolocationError = useCallback((error) => {
    console.error('Errore geolocalizzazione:', error);
    setLoadingDistance(false);
    setUserLocation(null);
  }, []);

  const updateUserLocation = useCallback((position) => {
    const { latitude, longitude } = position.coords;
    setUserLocation({ latitude, longitude });
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLoadingDistance(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 30000
    };

    const watchId = navigator.geolocation.watchPosition(
      updateUserLocation,
      handleGeolocationError,
      options
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [updateUserLocation, handleGeolocationError]);

  useEffect(() => {
    if (!userLocation || !poi?.field_geolocalizzazione?.[0]) return;

    const distance = calculateDistance(
      userLocation.latitude,
      userLocation.longitude, 
      poi.field_geolocalizzazione[0].lat,
      poi.field_geolocalizzazione[0].lng
    );
    
    setUserDistance(distance);
    setLoadingDistance(false);
  }, [userLocation, poi]);

  useEffect(() => {
    if (!loading && poi && carouselRef.current && poi.field_immagini?.length > 0) {
      new window.bootstrap.Carousel(carouselRef.current, {
        interval: 10000
      });
    }
  }, [loading, poi]);

  const loadFocusDetails = async (poi) => {
    if (!poi?.field_focus?.length) return;
    
    try {
      // Ottimizzazione: Raccogliere tutte le promesse e eseguirle in parallelo
      const focusPromises = poi.field_focus.map(async focus => {
        const focusUrl = config.API_ENDPOINTS.getNode(focus.target_id, selectedLang);
        
        // Verifica se i dati del focus sono già in cache
        let focusResponse;
        if (cache.current.has(focusUrl)) {
          focusResponse = cache.current.get(focusUrl);
        } else {
          focusResponse = await fetchWithRetry(focusUrl);
        }
        
        let taxonomyData = null;
        if (focusResponse.field_tipologia_focus?.[0]?.target_id) {
          const taxonomyId = focusResponse.field_tipologia_focus[0].target_id;
          const taxonomyUrl = config.API_ENDPOINTS.getTaxonomyTerm(taxonomyId, selectedLang);
          
          if (cache.current.has(taxonomyUrl)) {
            taxonomyData = cache.current.get(taxonomyUrl);
          } else {
            taxonomyData = await fetchWithRetry(taxonomyUrl);
          }
        }

        return {
          id: focus.target_id,
          details: focusResponse,
          taxonomy: taxonomyData
        };
      });

      // Esecuzione in parallelo di tutte le richieste
      const focusResults = await Promise.all(focusPromises);
      
      // Aggiornamento dello stato con tutti i risultati
      const focusDetailsMap = {};
      focusResults.forEach(result => {
        focusDetailsMap[result.id] = {
          details: result.details,
          taxonomy: result.taxonomy
        };
      });

      setFocusDetails(focusDetailsMap);
    } catch (error) {
      console.error('Errore durante il recupero dei dettagli dei focus:', error);
    }
  };

  // Ottimizzazione del loadAllPois
  const loadAllPois = async () => {
    try {
      // Verifica se sono disponibili dati in cache
      const cacheKey = config.API_ENDPOINTS.getPOIList(selectedLang);
      if (cache.current.has(cacheKey)) {
        const cachedPois = cache.current.get(cacheKey);
        setAllPois(cachedPois);
        return;
      }
      
      // Altrimenti carica i dati
      const allPoisResponse = await fetchWithRetry(cacheKey);
      setAllPois(allPoisResponse);

      // Ottimizzazione: Estrai tutti gli ID di tassonomia unici prima di fare le richieste
      const uniqueTaxonomyIds = new Set();
      
      allPoisResponse.forEach(poi => {
        const taxonomyId = poi.field_tipologia_poi?.[0]?.target_id;
        if (taxonomyId && !loadedTaxonomyIds.current.has(taxonomyId)) {
          uniqueTaxonomyIds.add(taxonomyId);
        }
      });

      // Esegui le richieste di tassonomia in parallelo
      if (uniqueTaxonomyIds.size > 0) {
        const taxonomyPromises = Array.from(uniqueTaxonomyIds).map(taxonomyId => {
          const taxonomyUrl = config.API_ENDPOINTS.getTaxonomyTerm(taxonomyId, selectedLang);
          
          // Se già in cache, skip
          if (cache.current.has(taxonomyUrl)) {
            const cachedTerm = cache.current.get(taxonomyUrl);
            setTaxonomyData(prev => ({
              ...prev,
              [cachedTerm.tid[0].value]: {
                title: cachedTerm.name[0].value,
                icon: cachedTerm.field_icona?.[0]?.url || null,
                pin: cachedTerm.field_pin?.[0]?.url || null,
              },
            }));
            loadedTaxonomyIds.current.add(taxonomyId);
            return Promise.resolve();
          }
          
          // Altrimenti carica e salva in cache
          return fetchWithRetry(taxonomyUrl)
            .then(response => {
              const term = response;
              setTaxonomyData(prev => ({
                ...prev,
                [term.tid[0].value]: {
                  title: term.name[0].value,
                  icon: term.field_icona?.[0]?.url || null,
                  pin: term.field_pin?.[0]?.url || null,
                },
              }));
              loadedTaxonomyIds.current.add(taxonomyId);
            })
            .catch(error => {
              console.error(`Errore nel caricamento della tassonomia ${taxonomyId}:`, error);
              loadedTaxonomyIds.current.delete(taxonomyId);
            });
        });

        await Promise.all(taxonomyPromises);
      }
    } catch (error) {
      console.error('Errore durante il caricamento di tutti i POI:', error);
    }
  };

  // Ottimizzazione del caricamento dei percorsi associati
  const loadAssociatedRoutes = useCallback(async () => {
    if (loadingRoutes) return;
    
    const routesUrl = config.API_ENDPOINTS.getPercorsiList(selectedLang);
    
    // Verifica se i dati sono già disponibili in cache
    if (cache.current.has(routesUrl)) {
      const cachedRoutes = cache.current.get(routesUrl);
      const filteredRoutes = cachedRoutes.filter(route => 
        route.field_poi?.some(poi => String(poi.target_id) === String(nid)));
      
      setAssociatedRoute(filteredRoutes);
      return;
    }
    
    setLoadingRoutes(true);
    try {
      const routesResponse = await fetchWithRetry(routesUrl);
      
      const associatedRoutes = routesResponse.filter(route => 
        route.field_poi?.some(poi => String(poi.target_id) === String(nid)));

      setAssociatedRoute(associatedRoutes);
    } catch (error) {
      console.error('Errore durante il caricamento dei percorsi:', error);
      setAssociatedRoute([]);
    } finally {
      setLoadingRoutes(false);
    }
  }, [nid, selectedLang, loadingRoutes, fetchWithRetry]);

  // Aggiungo un effetto per il lazy loading dei percorsi
  useEffect(() => {
    if (!loading && poi) {
      // Reset associatedRoute solo quando cambia il POI
      setAssociatedRoute(null);
      loadAssociatedRoutes();
    }
  }, [nid, loading, poi, loadAssociatedRoutes]);

  const handleShare = async () => {
    try {
      await navigator.share({
        title: poi.title[0].value,
        url: window.location.href
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleFocusClick = (focusId) => {
    const focusData = focusDetails[focusId];
    if (focusData) {
      setActiveFocus(focusData.details);
      setFocusPanelOpen(true);
    }
  };

  const handleFocusPanelClose = () => {
    setFocusPanelOpen(false);
    setActiveFocus(null);
  };

  const handleMapRedirect = () => {
    setMapPanelOpen(true);
  };

  const handleNavigate = () => {
    if (poi?.field_geolocalizzazione?.[0]) {
      const { lat, lng } = poi.field_geolocalizzazione[0];
      navigate(lat, lng);
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (!poi) return <p>{t('poiNonTrovato')}</p>;

  const hasImages = poi.field_immagini?.length > 0;
  const hasAddress = poi.field_indirizzo?.[0]?.value;
  const hasHours = poi.field_orari?.[0]?.value;
  const hasCosts = poi.field_info_costi?.[0]?.value;
  const hasWebsites = poi.field_sito_web?.length > 0;
  const hasContacts = poi.field_contatti && Array.isArray(poi.field_contatti) && poi.field_contatti.length > 0;

  return (
    <div className="poi-details">
      <BackButton />
      <button 
        onClick={handleShare}
        aria-label="{t('condividi')}"
        className="btn bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm ratio-1x1 goback"
        style={{ 
          position: 'absolute',
          top: '70px',
          right: '8px',
          left: 'inherit'
        }}
      >
        <Icon name="share" />
      </button>
      
      {hasImages && (
        <div id="carouselExampleIndicators" className="carousel slide" data-bs-ride="carousel" ref={carouselRef}>
          {poi.field_immagini?.length > 1 && (
            <ol className="carousel-indicators">
              {poi.field_immagini?.map((_, index) => (
                <li
                  key={index}
                  data-bs-target="#carouselExampleIndicators"
                  data-bs-slide-to={index}
                  className={index === 0 ? 'active' : ''}
                  aria-current={index === 0 ? 'true' : undefined}
                />
              ))}
            </ol>
          )}
          <div className="carousel-inner">
            {getOptimizedPoiImages(poi, 'max_650x650')?.map((immagine, index) => (
              <div
                key={index}
                className={`carousel-item ${index === 0 ? 'active' : ''}`}
              >
                <img
                  src={immagine.url}
                  className="carousel-image"
                  alt={immagine.alt || `Immagine ${index + 1}`}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="btn-container d-flex justify-content-between mx-3 sticky-top" 
           style={{ top: '62px', zIndex: 1, marginTop: '-10px' }}>
        <button 
          className="btn bg-white border rounded-pill flex-fill mx-1 d-flex align-items-center justify-content-center shadow-sm" 
          onClick={() => setShowTTS(true)}
        >
          {t('ascolta')}<Icon name="graphic_eq" className="ms-1"/>
        </button>
        <button 
          className="btn bg-white border rounded-pill flex-fill mx-1 d-flex align-items-center justify-content-center shadow-sm" 
          onClick={handleMapRedirect}
        >
          {t('trova')} <Icon name="pin_drop" className="ms-1"/>
        </button>
        <button 
          className="btn bg-white border rounded-pill flex-fill mx-1 d-flex align-items-center justify-content-center shadow-sm" 
          onClick={handleNavigate}
        >
          {t('naviga')} <Icon name="directions" className="ms-1" />
        </button>
      </div>

      <section className="container description text-content pb-0">
        {taxonomyPOIData[poi.field_tipologia_poi?.[0]?.target_id] && (
          <div className="d-flex align-items-start mb-2 mt-3" style={{ gap: '8px' }}>
            <div className="flex-shrink-0" style={{ width: '35%' }}>
              <span className="text-muted small">
                <Icon name="schedule" className="align-bottom me-1" style={{ fontSize: '1.1rem' }} />
                {readingTime}
              </span>
            </div>
            <div className="d-flex align-items-center flex-grow-1 justify-content-end" style={{ width: '65%' }}>
              {taxonomyPOIData[poi.field_tipologia_poi[0].target_id].icon && (
                <img 
                  src={taxonomyPOIData[poi.field_tipologia_poi[0].target_id].icon} 
                  alt="Icona categoria" 
                  style={{ width: '25px', height: '25px', marginRight: '4px', flexShrink: 0}} 
                />
              )}
              <span className="text-break" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                {taxonomyPOIData[poi.field_tipologia_poi[0].target_id].title}
              </span>
            </div>
          </div>
        )}

        <h1 className="mb-2">{poi.title[0].value}</h1>
        <p className="mb-4" style={{ fontSize: '0.875rem' }}>
          <Icon name="near_me" className="align-bottom me-1" style={{ fontSize: '1.1rem' }} />
          {t('distanza')} {loadingDistance ? (
            <div className="spinner-border spinner-border-sm ms-1" style={{ width: '12px', height: '12px' }} role="status">
              <span className="visually-hidden">{t('caricamento')}</span>
            </div>
          ) : userDistance !== null ? (
            <><span className="fw-bold">{userDistance.toFixed(1)}</span> <span className="fw-bold">km</span></>
          ) : null}
        </p>

        <div className="info-box mb-3  border align-items-center">
          <LocalIcon icon="info" style={{ width: '32px', marginRight: '10px' }} />
          <div 
            className="info-text"
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(poi.field_descrizione_breve[0]?.value || '') 
            }} 
          />
        </div>

        <div
          className="poi-body mb-4"
          dangerouslySetInnerHTML={{ 
            __html: DOMPurify.sanitize(poi.body[0]?.value || '') 
          }}
        />

        {poi.field_focus && poi.field_focus.length > 0 && (
          <>
            <h3 className="mb-3">{t('approfondimenti')}</h3>
            {poi.field_focus.map((focus, index) => {
              const focusData = focusDetails[focus.target_id];
              const details = focusData?.details;
              const taxonomy = focusData?.taxonomy;

              return (
                <div
                  key={index}
                  className="nody-focus-box d-flex align-items-center justify-content-between p-3 mb-3 border rounded bg-white shadow-sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleFocusClick(focus.target_id)}
                >
                  <div className="d-flex align-items-center">
                    {taxonomy?.field_icona?.[0]?.url && (
                      <LocalIcon 
                        icon={getFocusIconName(taxonomy)} 
                        style={{ width: '24px', height: '24px', marginRight: '1rem', color: 'var(--color-text-primary)', flexShrink: 0}} 
                      />
                    )}
                    <div>
                      <h4 className="mb-1 fw-semibold p-0" style={{ fontSize: '1.1rem' }}>
                        {details?.title?.[0]?.value || t('testoApprofondimento')}
                      </h4>
                      {details?.field_descrizione_breve?.[0]?.value && (
                        <div 
                          className="small"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: '2',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              details.field_descrizione_breve[0].value
                              .replace(/<p/g, '<p class="mb-0" style="font-size: .875rem;"')
                            )
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <Icon name="chevron_right" className="text-muted" style={{ fontSize: '1.2rem' }} />
                </div>
              );
            })}
            
            <FocusPanel 
              isOpen={focusPanelOpen}
              onClose={handleFocusPanelClose}
              focus={activeFocus}
              taxonomyData={focusDetails[activeFocus?.nid[0]?.value]?.taxonomy}
            />
          </>
        )}

        {hasAddress && (
          <>
            <h3 className="mb-1">{t('indirizzo')}</h3>
            <address>
              <p className="mb-4">{poi.field_indirizzo[0].value}</p>
            </address>
          </>
        )}

        {hasHours && (
          <>
            <h3 className="mb-1">{t('orari')}</h3>
            <div 
              className="mb-4"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(poi.field_orari[0].value) 
              }}
            />
          </>
        )}

        {hasCosts && (
          <>
            <h3 className="mb-1">{t('costi')}</h3>
            <div 
              className="mb-4"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(poi.field_info_costi[0].value) 
              }}
            />
          </>
        )}

        {loadingRoutes ? (
          <div className="mb-4 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('caricamento')}</span>
            </div>
          </div>
        ) : associatedRoute && associatedRoute.length > 0 && (
          <div className="mb-4">
            <RouteBox routes={associatedRoute} />
          </div>
        )}

        {config.components.transport && (
        <TransportPanel 
          isOpen={transportPanelOpen}
          onClose={() => setTransportPanelOpen(false)}
          poi={poi}
        />
        )}

        {config.components.transport && poi.field_geolocalizzazione && poi.field_geolocalizzazione[0] && (
          <>
            <h3 className="mb-3">{t('comeArrivare')}</h3>
            <div
              className="nody-focus-box d-flex align-items-center justify-content-between p-3 mb-3 border rounded bg-white shadow-sm"
              style={{ cursor: 'pointer' }}
              onClick={() => setTransportPanelOpen(true)}
            >
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <Icon name="directions_bus" style={{ fontSize: '32px' }} />
                </div>
                <div>
                  <h4 className="mb-1 fw-semibold p-0" style={{ fontSize: '1.1rem' }}>
                  {t('trasportoPubblico')}
                  </h4>
                  <p className="small">
                  {t('fermateVicine')}
                  </p>
                </div>
              </div>
              <Icon name="chevron_right" className="text-muted" style={{ fontSize: '1.2rem' }} />
            </div>
          </>
        )}

        {hasWebsites && (
          <div className="mb-1">
            <h3 className="mb-1">{t('sitiWeb')}</h3>
            <ul className="list-group list-group-flush">
              {poi.field_sito_web.map((site, index) => (
                <li key={index} className="list-group-item">
                  <a 
                    href={site.uri} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="fw-semibold d-flex justify-content-between align-items-center rounded"
                  >
                    <span>{site.title || site.uri}</span>
                    <LocalIcon icon="chevron_right" className="ms-2" style={{ fontSize: '1.2em' }} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {hasContacts && Array.isArray(contacts) && contacts.filter(contact => {
          const label = contact.field_etichetta_contatto?.value || '';
          const value = contact.field_valore_contatto?.value || '';
          return label.trim() !== '' || value.trim() !== '';
        }).length > 0 && (
          <div className="mb-1">
            <h3 className="mb-1">{t('contatti')}</h3>
            <ul className="list-group list-group-flush">
              {contacts.filter(contact => {
                const label = contact.field_etichetta_contatto?.value || '';
                const value = contact.field_valore_contatto?.value || '';
                return label.trim() !== '' || value.trim() !== '';
              }).map((contact, index) => {
                const label = contact.field_etichetta_contatto?.value || '';
                const value = contact.field_valore_contatto?.value || '';
                
                if (label.trim() === '' && value.trim() === '') {
                  return null;
                }
                
                let url = '';
                let target = '_blank'; // Imposta target="_blank" come default
                
                if (value.includes('@')) {
                  url = `mailto:${value}`;
                  target = '_self'; // Override per le email
                } 
                else if (/^[\+]?[0-9\s\-()]+$/.test(value)) {
                  url = `tel:${value.replace(/\s+/g, '')}`;
                  target = '_self'; // Override per i numeri di telefono
                } 
                else {
                  url = value.startsWith('http') ? value : `https://${value}`;
                  // Mantiene target="_blank" per i link web
                }
                
                return (
                  <li key={index} className="list-group-item">
                    <a
                      href={url}
                      target={target}
                      rel="noopener noreferrer"
                      className="fw-semibold d-flex justify-content-between align-items-center rounded"
                    >
                      <span>{label || value}</span>
                      <LocalIcon icon="chevron_right" className="ms-2" style={{ fontSize: '1.2em' }} />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {showTTS && (
          <TTSComponent 
            text={
              [
                poi.title[0].value,
                DOMPurify.sanitize(poi.field_descrizione_breve[0]?.value || '')
                  .replace(/&nbsp;|&#160;/gi, ' ')
                  .replace(/(<\s*br\s*\/?>|<\/p\s*>)/gi, '|||BREAK|||')
                  .replace(/<[^>]+>/g, '')
                  .replace(/\s*\|\|\|BREAK\|\|\|/g, '|||BREAK|||')
                  .replace(/([.!?…])\s*\|\|\|BREAK\|\|\|/g, '$1 ')
                  .replace(/\|\|\|BREAK\|\|\|/g, '. '),
                DOMPurify.sanitize(poi.body[0]?.value || '')
                  .replace(/&nbsp;|&#160;/gi, ' ')
                  .replace(/(<\s*br\s*\/?>|<\/p\s*>)/gi, '|||BREAK|||')
                  .replace(/<[^>]+>/g, '')
                  .replace(/\s*\|\|\|BREAK\|\|\|/g, '|||BREAK|||')
                  .replace(/([.!?…])\s*\|\|\|BREAK\|\|\|/g, '$1 ')
                  .replace(/\|\|\|BREAK\|\|\|/g, '. ')
              ]
              .filter(Boolean)
              .map(s => s.trim())
              .reduce((acc, curr) => {
                if (!acc) return curr;
                if (/(\.\.\.|…|[.!?]|\.\s)\s*$/.test(acc)) {
                  return acc + ' ' + curr;
                }
                return acc + '. ' + curr;
              }, '')
            }
            title={poi.title[0].value}
            onClose={() => setShowTTS(false)}
            language={selectedLang}
          />
        )}
      </section>
      <MapPanel 
          isOpen={mapPanelOpen}
          onClose={() => setMapPanelOpen(false)}
          poi={poi}
        />

      {!loading && config.components.poiSlider && config.components.nearbyPlaces.enabled && (
        <PoiSlider 
          key={nid}
          currentPoi={poi}
          allPois={allPois}
          taxonomyData={taxonomyData}
          onPoiChange={() => {
            scrollWithOffset();
            setShowTTS(false);
          }}
        />
      )}   
    </div>
  );
});

export default PoiDetails;